import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Sparkles } from "lucide-react";
import SEO from "@/components/SEO";
import { api, resolveImage } from "@/lib/api";

// Each gift guide is defined here for now (deferred: pull from a backend
// registry endpoint so non-devs can edit — see hard-list in ROADMAP).
const GUIDES = {
  "for-him": {
    h1: "Funny Car Gifts For Him — Booty Breeze",
    hero: "For the guy who already has everything.",
    intro: "The guys who are hardest to shop for usually don't want more gear — they want something with personality. Under $10, lands the joke, and lasts a month. That's Booty Breeze.",
    meta: {
      title: "Funny Car Gifts For Him — Under $10 & Actually Used",
      desc: "The best funny adult car gifts for him — hand-poured novelty air fresheners under $10 that actually smell good and last 3–4 weeks. Free shipping over $10.",
    },
    faqs: [
      { q: "What's the best funny car gift under $10?", a: "A Booty Breeze hand-poured novelty freshener at $8.99. Grab two and you also unlock free shipping — perfect for a stocking-stuffer or gag gift that actually gets used." },
      { q: "Are they inappropriate for a work gift exchange?", a: "The outer packaging is discreet; the branding is inside the box. That said, they're an 18+ adult-tone brand, so a Secret Santa with the guys is a hit — a workplace bake-sale is not." },
      { q: "Do they actually smell good?", a: "Yes — fragrance is our whole point. Each scent is proofed in-car by the team, not just on a paper strip. Bold day one, still going three weeks in." },
    ],
  },
  "funny-car-accessories": {
    h1: "Funny Car Accessories That Don't Look Cheap",
    hero: "Gag energy. Zero dollar-store aesthetic.",
    intro: "Most funny car accessories share one problem: they look funny for the first 5 minutes, then just look cheap for the next 5 years. Ours don't. Booty Breeze fresheners are printed on a laminated body, hand-poured, and designed like a real product — the joke is on the branding, not the build.",
    meta: {
      title: "Funny Car Accessories That Don't Look Cheap | Booty Breeze",
      desc: "The best funny adult car accessories that don't look like a gag from the dollar-store rack. Hand-poured, small-batch, and cheaper than a tank of gas.",
    },
    faqs: [
      { q: "Aren't most funny car accessories junk?", a: "Yes — that's the whole reason Booty Breeze exists. Cardboard printed on both sides, dies in a week. Ours are hand-poured on a laminated body and cured before packaging." },
      { q: "How long do the fresheners last?", a: "3–4 weeks per freshener in a daily driver — roughly 4× the lifespan of a cheap tree freshener." },
      { q: "Will my passenger actually laugh?", a: "That's part of the point. But they should also compliment the scent — the joke lands twice." },
    ],
  },
  "under-20": {
    h1: "Adult Novelty Gifts Under $20",
    hero: "Under twenty. Delivered smiling.",
    intro: "Under $20 is the sweet spot for stocking stuffers, white elephants, and Secret Santa gifts. Booty Breeze fresheners come in at $8.99 each — grab two, hit free shipping, and you've got a giftable pair with room to spare on the budget.",
    meta: {
      title: "Adult Novelty Gifts Under $20 — Booty Breeze Picks",
      desc: "The best adult novelty car gifts under $20 — hand-poured fresheners at $8.99 each, free shipping over $10, ships discreetly from the USA.",
    },
    faqs: [
      { q: "What's the best adult gag gift under $20?", a: "A pair of Booty Breeze fresheners ($8.99 × 2 = $17.98) hits free-shipping and gives the recipient two months of scent rotation. Bigger impact than one-and-done." },
      { q: "Do they ship in time for gifting?", a: "Orders usually leave our warehouse in 1–2 business days with tracked USPS." },
      { q: "Discreet packaging?", a: "Yes — plain outer packaging. The brand only shows once the box is opened." },
    ],
  },
};

export default function GiftGuide() {
  const { slug } = useParams();
  const [products, setProducts] = useState([]);
  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data.products || [])).catch(() => {});
  }, []);

  const g = GUIDES[slug];
  if (!g) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="guide-not-found">
        <div className="font-display text-4xl text-white mb-4">Guide not found.</div>
        <Link to="/gifts" className="text-[#FF3D8B] uppercase text-sm tracking-widest">Back to gift guides</Link>
      </div>
    );
  }

  const url = `https://bootybreeze.com/gifts/${slug}`;
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": g.h1,
      "url": url,
      "itemListElement": products.slice(0, 6).map((p, i) => ({
        "@type": "ListItem",
        "position": i + 1,
        "url": `https://bootybreeze.com/product/${p.id}`,
        "name": p.name,
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": g.faqs.map(({ q, a }) => ({
        "@type": "Question",
        "name": q,
        "acceptedAnswer": { "@type": "Answer", "text": a },
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home",  "item": "https://bootybreeze.com/" },
        { "@type": "ListItem", "position": 2, "name": "Gifts", "item": "https://bootybreeze.com/gifts" },
        { "@type": "ListItem", "position": 3, "name": g.h1,    "item": url },
      ],
    },
  ];

  return (
    <div className="min-h-screen" data-testid={`gift-guide-page-${slug}`}>
      <SEO title={g.meta.title} description={g.meta.desc} path={`/gifts/${slug}`} jsonLd={jsonLd} />

      <section className="max-w-3xl mx-auto px-6 sm:px-10 pt-14 pb-10">
        <Link
          to="/gifts"
          className="inline-flex items-center gap-2 text-white/50 hover:text-[#FF3D8B] text-xs uppercase tracking-widest mb-6 transition-colors"
        >
          <ArrowLeft size={14} /> All gift guides
        </Link>
        <div className="text-[#A855F7] uppercase text-xs tracking-widest">Gift Guide</div>
        <h1 className="font-display text-4xl sm:text-6xl text-white mt-3 leading-tight">
          {g.h1.split(" — ")[0]}
        </h1>
        <p className="text-white/70 mt-6 text-lg leading-relaxed">{g.hero}</p>
        <p className="text-white/60 mt-4 leading-relaxed">{g.intro}</p>
      </section>

      {/* PRODUCT GRID */}
      {products.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 sm:px-10 py-10 border-t border-white/10">
          <div className="text-[#A855F7] uppercase text-xs tracking-widest mb-4">Our picks</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="guide-products">
            {products.slice(0, 6).map((p) => (
              <Link
                key={p.id}
                to={`/product/${p.id}`}
                className="group rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden hover:border-[#FF3D8B]/40 transition-colors"
              >
                <img
                  src={resolveImage(p.image_url, 500)}
                  alt={p.name}
                  loading="lazy"
                  className="w-full aspect-square object-cover"
                />
                <div className="p-4">
                  <div className="text-white font-semibold">{p.name}</div>
                  <div className="text-white/50 text-xs mt-1">
                    ${p.price?.toFixed(2)} · Hand-poured, 3–4 weeks
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-6 sm:px-10 py-14 border-t border-white/10">
        <h2 className="font-display text-3xl text-white">Common questions</h2>
        <div className="mt-6 space-y-3">
          {g.faqs.map(({ q, a }, i) => (
            <details
              key={q}
              data-testid={`guide-faq-${i}`}
              className="group rounded-2xl border border-white/10 bg-white/[0.02] hover:border-[#FF3D8B]/30 transition-colors"
            >
              <summary className="cursor-pointer list-none px-6 py-4 flex justify-between items-start gap-4">
                <span className="font-display text-base text-white leading-tight">{q}</span>
                <span className="text-[#FF3D8B] text-xl leading-none group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="px-6 pb-5 text-white/70 leading-relaxed text-sm">{a}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 sm:px-10 py-16 text-center border-t border-white/10">
        <Sparkles className="mx-auto text-[#A855F7]" size={22} />
        <h2 className="font-display text-3xl text-white mt-4">Grab two, shipping's on us.</h2>
        <Link
          to="/shop"
          data-testid="guide-shop-cta"
          className="pulse-pink mt-6 inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#FF3D8B] text-black font-bold uppercase tracking-wider text-sm hover:bg-[#ff5ba0] transition-colors"
        >
          Shop the collection <ArrowRight size={18} />
        </Link>
      </section>
    </div>
  );
}
